// frontend/src/components/features/ScorePad.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { authService } from "../../services/auth";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import logger from "../../utils/logger";
import ParticipantTypeToggle from "../common/ParticipantTypeToggle";
import GameSearchDropdown from "../common/GameSearchDropdown";
import "./ModalComponents.css";

// Generate unique IDs for players, phases, and categories
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const STORAGE_PREFIX = "pista_scorepad";

function ScorePad({ game, user, onClose, onLogSession, showBanner }) {
  // Game selection state - only used when no `game` prop is supplied (e.g. when
  // ScorePad is opened standalone, without a game already chosen elsewhere).
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameSearchQuery, setGameSearchQuery] = useState("");
  const [showGameDropdown, setShowGameDropdown] = useState(false);
  const [gameSearchResults, setGameSearchResults] = useState([]);
  const [gameSearchLoading, setGameSearchLoading] = useState(false);

  // The game actually used by the scoring pad: the one passed in via props,
  // or the one picked from the in-modal game selector.
  const activeGame = game || selectedGame;

  // Players state - includes current user by default
  const [players, setPlayers] = useState([
    {
      id: generateId(),
      user_id: user?.id,
      username: user?.name || user?.username || "You",
      isExisting: true,
      isAutomata: false,
    },
  ]);

  // Phases/Ages with nested categories
  const [phases, setPhases] = useState([
    {
      id: generateId(),
      name: "Phase 1",
      categories: [{ id: generateId(), name: "Category 1" }],
    },
  ]);
  const [activePhaseId, setActivePhaseId] = useState(phases[0]?.id);

  // Scores structure: { [playerId]: { [categoryId]: number } }
  const [scores, setScores] = useState({});
  const [flashingTotals, setFlashingTotals] = useState({});

  // User search state for adding participants
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);

  // Editing state for inline renaming
  const [editingPhaseId, setEditingPhaseId] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editValue, setEditValue] = useState("");

  // Track which score cell is actively being edited (shows calculator only for that cell)
  const [activeScoreCell, setActiveScoreCell] = useState(null);

  // Refs for click outside handling
  const searchContainerRef = useRef(null);
  const scoreCellRef = useRef(null);

  // Persist the latest state so it is available on unmount/close without re-running effects
  const stateRef = useRef({
    players,
    phases,
    scores,
    activePhaseId,
    selectedGame,
    gameSearchQuery,
  });
  useEffect(() => {
    stateRef.current = {
      players,
      phases,
      scores,
      activePhaseId,
      selectedGame,
      gameSearchQuery,
    };
  }, [players, phases, scores, activePhaseId, selectedGame, gameSearchQuery]);

  const storageKey = useMemo(() => {
    const gameId = activeGame?.id || activeGame?.game_id;
    return user?.id && gameId ? `${STORAGE_PREFIX}:${user.id}:${gameId}` : null;
  }, [activeGame, user]);
  const storageKeyRef = useRef(storageKey);
  useEffect(() => {
    storageKeyRef.current = storageKey;
  }, [storageKey]);

  const persistState = useCallback(() => {
    if (!storageKeyRef.current) return;
    try {
      localStorage.setItem(storageKeyRef.current, JSON.stringify(stateRef.current));
    } catch (e) {
      logger.error("Failed to persist ScorePad state:", e);
    }
  }, []);

  // Restore saved state for the current game, if any
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      const hasState =
        Array.isArray(saved.players) && saved.players.length > 0 &&
        Array.isArray(saved.phases) && saved.phases.length > 0;
      if (!hasState) return;

      setPlayers(saved.players);
      setPhases(saved.phases);
      setScores(saved.scores || {});
      const savedActive = saved.phases.some((p) => p.id === saved.activePhaseId)
        ? saved.activePhaseId
        : saved.phases[0].id;
      setActivePhaseId(savedActive);
      if (!game && saved.selectedGame) {
        setSelectedGame(saved.selectedGame);
        setGameSearchQuery(saved.selectedGame.name || "");
      }
    } catch (e) {
      logger.error("Failed to load persisted ScorePad state:", e);
    }
  }, [storageKey, game]);

  // Debounced user search
  const searchUsers = useCallback(async (query) => {
    if (!query.trim()) {
      setUserSearchResults([]);
      setShowUserDropdown(false);
      return;
    }

    try {
      const res = await httpRequest(
        `${API_BASE}/auth/users/search?q=${encodeURIComponent(query)}&limit=5`,
        {
          method: "GET",
          headers: authService.getAuthHeaders(),
        }
      );

      if (res.ok) {
        const data = await res.json();
        setUserSearchResults(data.users || []);
        setShowUserDropdown(data.users && data.users.length > 0);
      } else {
        setUserSearchResults([]);
        setShowUserDropdown(false);
      }
    } catch (error) {
      logger.error("User search failed:", error);
      setUserSearchResults([]);
    }
  }, []);

  // Debounce search effect
  useEffect(() => {
    if (userSearchQuery) {
      const timer = setTimeout(() => {
        searchUsers(userSearchQuery);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [userSearchQuery, searchUsers]);

  // Debounced game search (only relevant while no game has been chosen yet)
  const searchGames = useCallback(async (query) => {
    if (!query.trim()) {
      setGameSearchResults([]);
      setShowGameDropdown(false);
      return;
    }

    setGameSearchLoading(true);
    try {
      const res = await httpRequest(
        `${API_BASE}/games/search?q=${encodeURIComponent(query)}`,
        {
          method: "GET",
          headers: authService.getAuthHeaders(),
        }
      );

      if (res.ok) {
        const data = await res.json();
        setGameSearchResults(data.games || []);
        setShowGameDropdown(true);
      } else {
        setGameSearchResults([]);
        setShowGameDropdown(false);
      }
    } catch (error) {
      logger.error("Game search failed:", error);
      setGameSearchResults([]);
      setShowGameDropdown(false);
    } finally {
      setGameSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeGame) return;
    if (gameSearchQuery) {
      const timer = setTimeout(() => {
        searchGames(gameSearchQuery);
      }, 300);
      return () => clearTimeout(timer);
    }
    setGameSearchResults([]);
    setShowGameDropdown(false);
  }, [gameSearchQuery, searchGames, activeGame]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Dismiss score cell calculator when clicking outside
  useEffect(() => {
    const handleClickOutsideScore = (event) => {
      if (
        scoreCellRef.current &&
        !scoreCellRef.current.contains(event.target) &&
        !event.target.closest('.score-pad-score-buttons') &&
        !event.target.closest('.score-btn')
      ) {
        setActiveScoreCell(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideScore);
    return () => document.removeEventListener("mousedown", handleClickOutsideScore);
  }, []);

  // Add player from search
  const selectUser = (selectedUser) => {
    const username =
      selectedUser.username || selectedUser.name || selectedUser.display_name;

    // Check for duplicates
    const alreadyExists = players.some(
      (p) =>
        p.user_id === selectedUser.id ||
        p.username.toLowerCase() === username.toLowerCase()
    );

    if (alreadyExists) {
      showBanner("This user is already added", "error");
      return;
    }

    setPlayers((prev) => [
      ...prev,
      {
        id: generateId(),
        user_id: selectedUser.id,
        username: username,
        isExisting: true,
        isAutomata: false,
      },
    ]);

    setUserSearchQuery("");
    setShowUserDropdown(false);
    showBanner(`Added: ${username}`, "success");
  };

  // Add new player by username (non-existing user)
  const addNewPlayer = () => {
    const username = userSearchQuery.trim();
    if (!username) return;

    // Check for duplicates
    const alreadyExists = players.some(
      (p) => p.username.toLowerCase() === username.toLowerCase()
    );

    if (alreadyExists) {
      showBanner("This player is already added", "error");
      return;
    }

    setPlayers((prev) => [
      ...prev,
      {
        id: generateId(),
        username: username,
        isExisting: false,
        isAutomata: false,
      },
    ]);

    setUserSearchQuery("");
    setShowUserDropdown(false);
    showBanner(`Added: ${username}`, "success");
  };

  // Toggle player type between person and automata
  const togglePlayerType = (playerId, isAutomata) => {
    setPlayers((prev) =>
      prev.map((p) => (p.id === playerId ? { ...p, isAutomata } : p))
    );
  };

  // Remove player
  const removePlayer = (playerId) => {
    const target = players.find((p) => p.id === playerId);
    if (target?.user_id === user?.id) {
      showBanner("You can't remove yourself from the session", 'error');
      return;
    }
    setPlayers((prev) => prev.filter((p) => p.id !== playerId));
    // Clean up scores for removed player
    setScores((prev) => {
      const newScores = { ...prev };
      delete newScores[playerId];
      return newScores;
    });
  };

  // Add new phase/age and make it the active one
  const addPhase = () => {
    const newPhaseNum = phases.length + 1;
    const newPhaseId = generateId();
    const newPhase = {
      id: newPhaseId,
      name: `Phase ${newPhaseNum}`,
      categories: [{ id: generateId(), name: "Category 1" }],
    };
    setPhases((prev) => {
      // If this is the second phase and first phase is "Phase 1", rename it to "Phase 1"
      if (prev.length === 1 && prev[0].name === "Phase 1") {
        return [
          { ...prev[0], name: "Phase 1" },
          newPhase,
        ];
      }
      return [...prev, newPhase];
    });
    setActivePhaseId(newPhaseId);
  };

  // Remove phase
  const removePhase = (phaseId) => {
    setPhases((prev) => {
      const next = prev.filter((p) => p.id !== phaseId);
      // If the active phase was removed, fall back to the first remaining phase
      if (activePhaseId === phaseId && next.length > 0) {
        setActivePhaseId(next[0].id);
      }
      return next;
    });
    // Clean up scores for removed phase's categories
    const phase = phases.find((p) => p.id === phaseId);
    if (phase) {
      setScores((prev) => {
        const newScores = { ...prev };
        Object.keys(newScores).forEach((playerId) => {
          phase.categories.forEach((cat) => {
            delete newScores[playerId]?.[cat.id];
          });
        });
        return newScores;
      });
    }
  };

  // Rename phase (clicking the phase name also selects that phase)
  const startEditingPhase = (phase) => {
    setActivePhaseId(phase.id);
    setEditingPhaseId(phase.id);
    setEditValue(phase.name);
  };

  const savePhaseName = () => {
    if (editingPhaseId && editValue.trim()) {
      setPhases((prev) =>
        prev.map((p) =>
          p.id === editingPhaseId ? { ...p, name: editValue.trim() } : p
        )
      );
    }
    setEditingPhaseId(null);
    setEditValue("");
  };

  // Add category to phase
  const addCategory = (phaseId) => {
    setPhases((prev) =>
      prev.map((p) => {
        if (p.id !== phaseId) return p;

        const newCatNum = p.categories.length + 1;
        const newCategory = { id: generateId(), name: `Category ${newCatNum}` };

        // If this is the second category and first is "Category 1", rename it to "Category 1"
        if (p.categories.length === 1 && p.categories[0].name === "Category 1") {
          return {
            ...p,
            categories: [
              { ...p.categories[0], name: "Category 1" },
              newCategory,
            ],
          };
        }

        return {
          ...p,
          categories: [...p.categories, newCategory],
        };
      })
    );
  };

  // Remove category
  const removeCategory = (phaseId, categoryId) => {
    setPhases((prev) =>
      prev.map((p) =>
        p.id === phaseId
          ? { ...p, categories: p.categories.filter((c) => c.id !== categoryId) }
          : p
      )
    );
    // Clean up scores for removed category
    setScores((prev) => {
      const newScores = { ...prev };
      Object.keys(newScores).forEach((playerId) => {
        delete newScores[playerId]?.[categoryId];
      });
      return newScores;
    });
  };

  // Rename category
  const startEditingCategory = (category) => {
    setEditingCategoryId(category.id);
    setEditValue(category.name);
  };

  const saveCategoryName = () => {
    if (editingCategoryId && editValue.trim()) {
      setPhases((prev) =>
        prev.map((p) => ({
          ...p,
          categories: p.categories.map((c) =>
            c.id === editingCategoryId ? { ...c, name: editValue.trim() } : c
          ),
        }))
      );
    }
    setEditingCategoryId(null);
    setEditValue("");
  };

  // Update score
  const updateScore = (playerId, categoryId, delta) => {
    setScores((prev) => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [categoryId]: (prev[playerId]?.[categoryId] || 0) + delta,
      },
    }));
    setFlashingTotals((prev) => ({ ...prev, [playerId]: true }));
    setTimeout(() => {
      setFlashingTotals((prev) => ({ ...prev, [playerId]: false }));
    }, 600);
  };

  // Calculate total score for a player
  const getTotalScore = (playerId) => {
    const playerScores = scores[playerId] || {};
    return Object.values(playerScores).reduce((sum, score) => sum + score, 0);
  };

  // Format phase/category scores for SessionForm notes
  const formatScoreNotes = () => {
    return phases
      .map((phase) => {
        const categoryLines = phase.categories.map((category) => {
          const playerScores = players
            .map((player) => `${player.username}: ${scores[player.id]?.[category.id] || 0}`)
            .join(", ");
          return `  ${category.name}: ${playerScores}`;
        });
        return [`${phase.name}:`, ...categoryLines].join("\n");
      })
      .join("\n\n");
  };

  // Handle Log Session
  const handleLogSession = () => {
    if (!activeGame) {
      showBanner("Please select a game first", "error");
      return;
    }

    const mappedParticipants = players.map((player) => ({
      user_id: player.user_id,
      username: player.username,
      score: getTotalScore(player.id).toString(),
      isExisting: player.isExisting,
      isAutomata: player.isAutomata ?? false,
      rating: "",
      color: "",
    }));

    // Defensive: the owner must always be a participant
    const hasCurrentUser = mappedParticipants.some((p) => p.user_id === user?.id);
    const participants = hasCurrentUser
      ? mappedParticipants
      : [
          {
            user_id: user?.id,
            username: user?.name || user?.username || "You",
            score: "",
            isExisting: true,
            isAutomata: false,
            rating: "",
            color: "",
          },
          ...mappedParticipants,
        ];

    if (!participants.some((p) => p.user_id === user?.id)) {
      showBanner("You must be a participant to log this session", "error");
      return;
    }

    const scoreNotes = formatScoreNotes();

    // Clear saved ScorePad state once the session is submitted
    if (storageKeyRef.current) {
      try {
        localStorage.removeItem(storageKeyRef.current);
      } catch (e) {
        logger.error("Failed to clear persisted ScorePad state:", e);
      }
    }
    onLogSession({
      game: activeGame,
      participants,
      notes: scoreNotes,
    });
  };

  // Save progress and close the modal
  const closeScorePad = useCallback(() => {
    persistState();
    onClose();
  }, [onClose, persistState]);

  // Handle key press in search input
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Enter always adds the typed text as a new player, ignoring the
      // search dropdown. Selecting an existing user is only done by
      // clicking a dropdown result.
      addNewPlayer();
    }
  };

  return (
    <div className="modal-overlay" onClick={closeScorePad}>
      <div
        className="modal-content score-pad-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header score-pad-header">
          <h2 className="score-pad-title">Score - {activeGame?.name || "Game"}</h2>
          <button className="modal-close" onClick={closeScorePad}>
            ×
          </button>
        </div>

        <div className="modal-body score-pad-body">
          {/* Game Selection - only shown when no game was provided by the caller */}
          {!game && (
            <div className="score-pad-section">
              <label className="score-pad-label">Game</label>
              <GameSearchDropdown
                searchQuery={selectedGame ? selectedGame.name : gameSearchQuery}
                onSearchChange={(value) => {
                  setSelectedGame(null);
                  setGameSearchQuery(value);
                }}
                isVisible={showGameDropdown && !selectedGame}
                onVisibilityChange={setShowGameDropdown}
                searchResults={gameSearchResults}
                isLoading={gameSearchLoading}
                onGameSelect={(selected) => {
                  setSelectedGame(selected);
                  setGameSearchQuery(selected.name);
                  setShowGameDropdown(false);
                }}
                placeholder="Search for a game..."
                showImages={true}
              />
            </div>
          )}

          {/* User Search */}
          <div className="score-pad-section">
            <label className="score-pad-label">Add Players</label>
            <div className="user-search-container" ref={searchContainerRef}>
              <input
                type="text"
                placeholder="Search for users or type a name..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                onFocus={() => userSearchQuery && setShowUserDropdown(true)}
                onKeyPress={handleKeyPress}
                className="score-pad-search-input"
              />
              {showUserDropdown && userSearchResults.length > 0 && (
                <div className="user-search-dropdown">
                  {userSearchResults.map((u) => (
                    <div
                      key={u.id}
                      className="user-search-item"
                      onClick={() => selectUser(u)}
                    >
                      <span className="user-search-name">
                        {u.username || u.name || u.display_name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Players List */}
          <div className="score-pad-section">
            <label className="score-pad-label">Players</label>
            <div className="score-pad-players">
              {players.map((player) => (
                <div key={player.id} className="score-pad-player-row">
                  <ParticipantTypeToggle
                    isAutomata={player.isAutomata ?? false}
                    onChange={(isAutomata) => togglePlayerType(player.id, isAutomata)}
                  />
                  <span className="score-pad-player-name" title={player.username}>
                    {player.username}
                    {player.isExisting && (
                      <span className="score-pad-existing-badge">✓</span>
                    )}
                  </span>
                  {players.length > 1 && player.user_id !== user?.id && (
                    <button
                      className="score-pad-remove-btn"
                      onClick={() => removePlayer(player.id)}
                      title="Remove player"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Phases and Categories */}
          <div className="score-pad-section">
            <div className="score-pad-section-header">
              <label className="score-pad-label">Phases / Ages & Categories</label>
            </div>

            <div className="score-pad-phases">
              {phases.map((phase) => (
                <div
                  key={phase.id}
                  className={`score-pad-phase${
                    phase.id === activePhaseId ? ' score-pad-phase--active' : ''
                  }`}
                >
                  <div className="score-pad-phase-header">
                    {editingPhaseId === phase.id ? (
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={savePhaseName}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") savePhaseName();
                        }}
                        autoFocus
                        className="score-pad-edit-input"
                      />
                    ) : (
                      <span
                        className="score-pad-phase-name"
                        onClick={() => startEditingPhase(phase)}
                        title="Click to rename"
                      >
                        {phase.name}
                        <span className="score-pad-edit-icon" aria-hidden="true">✎</span>
                      </span>
                    )}
                    <div className="score-pad-phase-actions">
                      {phases.length > 1 && (
                        <button
                          className="score-pad-small-btn score-pad-danger"
                          onClick={() => removePhase(phase.id)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="score-pad-categories">
                    {phase.categories.map((category) => (
                      <div key={category.id} className="score-pad-category">
                        <div className="score-pad-category-header">
                          {editingCategoryId === category.id ? (
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveCategoryName}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") saveCategoryName();
                              }}
                              autoFocus
                              className="score-pad-edit-input"
                            />
                          ) : (
                            <span
                              className="score-pad-category-name"
                              onClick={() => startEditingCategory(category)}
                              title="Click to rename"
                            >
                              {category.name}
                              <span className="score-pad-edit-icon" aria-hidden="true">✎</span>
                            </span>
                          )}
                          {phase.categories.length > 1 && (
                            <button
                              className="score-pad-remove-btn-small"
                              onClick={() => removeCategory(phase.id, category.id)}
                              title="Remove category"
                            >
                              ×
                            </button>
                          )}
                        </div>

                        {/* Score buttons for each player */}
                        <div className="score-pad-scores-grid">
                          {players.map((player) => {
                            const cellKey = `${player.id}:${category.id}`;
                            const isCellActive = activeScoreCell === cellKey;
                            return (
                              <div
                                key={player.id}
                                ref={isCellActive ? scoreCellRef : null}
                                className={`score-pad-score-cell${
                                  isCellActive ? ' score-pad-score-cell--active' : ''
                                }`}
                                onClick={() => setActiveScoreCell(cellKey)}
                              >
                                <span className="score-pad-score-player" title={player.username}>
                                  {player.username}
                                </span>
                                <span className="score-pad-score-value">
                                  {scores[player.id]?.[category.id] || 0}
                                </span>
                                {isCellActive && (
                                  <div className="score-pad-score-buttons">
                                    <div className="score-column">
                                      <button
                                        className="score-btn score-btn-pos"
                                        onClick={(e) => { e.stopPropagation(); updateScore(player.id, category.id, 50); }}
                                      >
                                        +50
                                      </button>
                                      <button
                                        className="score-btn score-btn-neg"
                                        onClick={(e) => { e.stopPropagation(); updateScore(player.id, category.id, -50); }}
                                      >
                                        -50
                                      </button>
                                    </div>
                                    <div className="score-column">
                                      <button
                                        className="score-btn score-btn-pos"
                                        onClick={(e) => { e.stopPropagation(); updateScore(player.id, category.id, 10); }}
                                      >
                                        +10
                                      </button>
                                      <button
                                        className="score-btn score-btn-neg"
                                        onClick={(e) => { e.stopPropagation(); updateScore(player.id, category.id, -10); }}
                                      >
                                        -10
                                      </button>
                                    </div>
                                    <div className="score-column">
                                      <button
                                        className="score-btn score-btn-pos"
                                        onClick={(e) => { e.stopPropagation(); updateScore(player.id, category.id, 5); }}
                                      >
                                        +5
                                      </button>
                                      <button
                                        className="score-btn score-btn-neg"
                                        onClick={(e) => { e.stopPropagation(); updateScore(player.id, category.id, -5); }}
                                      >
                                        -5
                                      </button>
                                    </div>
                                    <div className="score-column">
                                      <button
                                        className="score-btn score-btn-pos"
                                        onClick={(e) => { e.stopPropagation(); updateScore(player.id, category.id, 1); }}
                                      >
                                        +1
                                      </button>
                                      <button
                                        className="score-btn score-btn-neg"
                                        onClick={(e) => { e.stopPropagation(); updateScore(player.id, category.id, -1); }}
                                      >
                                        -1
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-footer score-pad-footer">
          {/* Phase / Category controls above totals and save/cancel actions */}
          <div className="score-pad-footer-controls">
            <button className="score-pad-add-btn" onClick={addPhase}>
              + Phase
            </button>
            <button
              className="score-pad-add-btn score-pad-add-btn--secondary"
              onClick={() => addCategory(activePhaseId)}
            >
              + Category
            </button>
          </div>

          {/* Total scores */}
          <div className="score-pad-footer-totals">
            {players.map((player) => (
              <span
                key={player.id}
                className={`score-pad-footer-total${
                  flashingTotals[player.id] ? " score-pad-footer-total--flash" : ""
                }`}
              >
                {player.username}: <strong>{getTotalScore(player.id)}</strong>pts
              </span>
            ))}
          </div>

          <div className="score-pad-footer-actions">
            <button className="modal-button secondary" onClick={closeScorePad}>
              Cancel
            </button>
            <button
              className="modal-button primary"
              onClick={handleLogSession}
              disabled={!activeGame}
              title={!activeGame ? "Select a game first" : undefined}
            >
              Log Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScorePad;
